import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Image,
  IconButton,
  Link as ChakraLink,
  Stack,
  useDisclosure
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";

const NAV_LINKS = [
  { label: "Jak zagłosować", to: "/jak-zaglosowac" },
  { label: "Tajne głosowanie", to: "/tajne-glosowanie-zkp" },
  { label: "Bezpieczeństwo", to: "/tozsamosc-i-bezpieczenstwo" },
  { label: "Jak stworzyć głosowanie", to: "/jak-stworzyc-glosowanie" },
  { label: "FAQ", to: "/faq" },
  { label: "O nas", to: "/o-nas" }
];

const NavLink: React.FC<{ to: string; label: string; onClick?: () => void; color?: string }> = ({ to, label, onClick, color = "primary" }) => (
  <ChakraLink
    as={Link}
    to={to}
    onClick={onClick}
    fontSize="15px"
    fontWeight="600"
    color={color}
    whiteSpace="nowrap"
    _hover={{ textDecoration: "none", color: "accent" }}
  >
    {label}
  </ChakraLink>
);

// Chowa header przy scrollu w dół, pokazuje przy scrollu w górę; `scrolled` = czy zjechano z samej góry.
const useHideOnScroll = () => {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      if (y > lastY && y > 90) setHidden(true);
      else if (y < lastY) setHidden(false);
      setScrolled(y > 60);
      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { hidden, scrolled };
};

export const LandingHeader: React.FC<{ overlay?: boolean }> = ({ overlay }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { hidden, scrolled } = useHideOnScroll();
  // przezroczysty tylko na samej górze hero; po zjechaniu — normalny, solidny sticky
  const transparentTop = !!overlay && !scrolled;

  return (
    <Box
      as="header"
      position={overlay ? "fixed" : "sticky"}
      top={0}
      left={0}
      zIndex={100}
      w="100%"
      bg={transparentTop ? "transparent" : "rgba(255, 255, 255, 0.82)"}
      backdropFilter={transparentTop ? undefined : "saturate(180%) blur(12px)"}
      borderBottom={transparentTop ? "none" : "1px solid"}
      borderColor={transparentTop ? "transparent" : "rgba(67, 0, 152, 0.10)"}
      transform={hidden ? "translateY(-100%)" : "translateY(0)"}
      transition="transform 0.3s ease, background-color 0.25s ease"
    >
      <Flex
        w="100%"
        px={{ base: "20px", md: "40px", xl: "56px" }}
        h={{ base: "64px", md: "76px" }}
        align="center"
        justify="space-between"
        gap="24px"
      >
        <Link to="/" aria-label="Strona główna">
          <Image src={transparentTop ? "/LBP_Logo_White.svg" : "/LBP_Logo_Black.svg"} alt="lubbezpośrednio.pl" h={{ base: "28px", md: "34px" }} />
        </Link>

        {/* Desktop nav */}
        <HStack spacing={{ lg: "22px", xl: "32px" }} display={{ base: "none", lg: "flex" }}>
          {NAV_LINKS.map(l => (
            <NavLink key={l.to} {...l} color={transparentTop ? "white" : undefined} />
          ))}
        </HStack>

        {/* Desktop actions */}
        <HStack spacing="12px" display={{ base: "none", lg: "flex" }}>
          <Button
            as={Link}
            to="/logowanie"
            variant="ghost"
            color={transparentTop ? "white" : "primary"}
            fontWeight="700"
            borderRadius="12px"
            _hover={{ bg: transparentTop ? "rgba(255,255,255,0.14)" : "rgba(67, 0, 152, 0.06)" }}
          >
            Zaloguj się
          </Button>
          <Button
            as={Link}
            to="/rejestracja"
            bg="accent"
            color="white"
            fontWeight="700"
            borderRadius="12px"
            px="22px"
            whiteSpace="nowrap"
            _hover={{ bg: "#c41a3a" }}
            _active={{ bg: "#a8152f" }}
          >
            Zorganizuj głosowanie
          </Button>
        </HStack>

        {/* Mobile burger */}
        <IconButton
          aria-label="Menu"
          icon={<FiMenu size={24} />}
          onClick={onOpen}
          variant="ghost"
          color={transparentTop ? "white" : "primary"}
          display={{ base: "inline-flex", lg: "none" }}
        />
      </Flex>

      {/* Mobile drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="white">
          <Flex justify="space-between" align="center" px="20px" h="64px" borderBottom="1px solid" borderColor="rgba(67,0,152,0.1)">
            <Image src="/LBP_Logo_Black.svg" alt="logo" h="28px" />
            <IconButton aria-label="Zamknij" icon={<FiX size={24} />} onClick={onClose} variant="ghost" color="primary" />
          </Flex>
          <DrawerBody py="24px">
            <Stack spacing="20px" mb="32px">
              {NAV_LINKS.map(l => (
                <NavLink key={l.to} {...l} onClick={onClose} />
              ))}
            </Stack>
            <Stack spacing="12px">
              <Button as={Link} to="/logowanie" onClick={onClose} variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700">
                Zaloguj się
              </Button>
              <Button as={Link} to="/rejestracja" onClick={onClose} bg="accent" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#c41a3a" }}>
                Zorganizuj głosowanie
              </Button>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};
